// Загрузчик текстур: грузим картинки через <img>, переводим в ImageData,
// после первоначального использования держим в памяти как Texture с
// быстрым sampleNearest.
//
// Сами картинки — в client/public/textures/<name>.png. Vite раздаёт оттуда.

export interface Texture {
  width: number;
  height: number;
  pixels: Uint8ClampedArray; // RGBA, length = width*height*4
}

const cache = new Map<string, Texture>();

export async function loadTexture(name: string, ext: string = "png"): Promise<Texture> {
  const hasExt = /\.(png|jpg|jpeg|webp)$/i.test(name);
  const cacheKey = hasExt ? name : `${name}.${ext}`;
  const existing = cache.get(cacheKey);
  if (existing) return existing;
  const url = hasExt ? `/textures/${name}` : `/textures/${name}.${ext}`;
  const img = new Image();
  // Не ставим crossOrigin — картинки с того же origin, иначе браузер
  // требует CORS-заголовков от Caddy и onerror'ит без них.
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (e) => reject(new Error(`load ${url}: ${String(e)}`));
    img.src = url;
  });
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("canvas 2d ctx unavailable");
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, c.width, c.height);
  const tex: Texture = {
    width: c.width,
    height: c.height,
    pixels: data.data,
  };
  cache.set(cacheKey, tex);
  return tex;
}

/** Получить уже загруженную текстуру; null если её нет.
 *  Ключ — как в `loadTexture` / `buildMaterialAtlas` (`brick` или `levelpacks/.../x.png`). */
export function getTexture(name: string): Texture | null {
  const direct = cache.get(name);
  if (direct) return direct;
  if (!/\.(png|jpg|jpeg|webp)$/i.test(name)) {
    return cache.get(`${name}.png`) ?? cache.get(`${name}.jpg`) ?? null;
  }
  return null;
}

/** Залить уже загруженную CPU-текстуру в GPU как WebGL2 sampler.
 *  Возвращает GL-текстуру, готовую к использованию в шейдере.  */
export function uploadGLTexture(gl: WebGL2RenderingContext, tex: Texture, opts?: {
  wrap?: GLenum;       // gl.REPEAT | gl.CLAMP_TO_EDGE
  filter?: GLenum;     // gl.LINEAR | gl.NEAREST
  mipmap?: boolean;
}): WebGLTexture {
  const wrap = opts?.wrap ?? gl.REPEAT;
  const filter = opts?.filter ?? gl.LINEAR;
  const mipmap = opts?.mipmap ?? true;
  const t = gl.createTexture();
  if (!t) throw new Error("createTexture failed");
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texImage2D(
    gl.TEXTURE_2D, 0, gl.RGBA8,
    tex.width, tex.height, 0,
    gl.RGBA, gl.UNSIGNED_BYTE,
    tex.pixels,
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  if (mipmap) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.generateMipmap(gl.TEXTURE_2D);
    // Анизотропия: убирает «жёсткое» мыло/мерцание текстур под острыми
    // углами (стены лабиринта при взгляде вдоль). Бесплатно на десктопных
    // GPU, extension есть практически везде.
    const ext = gl.getExtension("EXT_texture_filter_anisotropic");
    if (ext) {
      const max = gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
      gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, max));
    }
  } else {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  }
  return t;
}

/** Псевдо-карта глубины: «красность» (бричок выпирает, mortar утоплен).
 * 0 = полностью утоплено, 1 = полная выпуклость. */
export function depthAt(t: Texture, u: number, v: number): number {
  // wrap UV
  u = u - Math.floor(u);
  v = v - Math.floor(v);
  const x = Math.min(t.width - 1, Math.floor(u * t.width));
  const y = Math.min(t.height - 1, Math.floor(v * t.height));
  const i = (y * t.width + x) * 4;
  const r = t.pixels[i], g = t.pixels[i + 1], b = t.pixels[i + 2];
  const redness = r - 0.5 * (g + b);
  if (redness < 30) return 0.15;
  return Math.min(1, redness / 130);
}

/** Цвет в [0..1]^3 по нормированным UV (nearest). */
export function sampleColor(t: Texture, u: number, v: number): [number, number, number] {
  u = u - Math.floor(u);
  v = v - Math.floor(v);
  const x = Math.min(t.width - 1, Math.floor(u * t.width));
  const y = Math.min(t.height - 1, Math.floor(v * t.height));
  const i = (y * t.width + x) * 4;
  return [t.pixels[i] / 255, t.pixels[i + 1] / 255, t.pixels[i + 2] / 255];
}

/** Размер одного слота в материальном атласе. Выбрано 512 как золотая
 *  середина: существующие brick/concrete/plaster/wood (1024×1024) теряют
 *  ~2× качества, но tile-материал это не ухудшает визуально; placeholder'ы
 *  (256×256) upscale'ятся без деталей, что ок для процедурной текстуры. */
export const MATERIAL_ATLAS_SLOT_SIZE = 512;

/** Построить горизонтальный атлас из набора текстур: slot[i] лежит по
 *  X = [i*SLOT, (i+1)*SLOT), все slots масштабированы к SLOT×SLOT через
 *  canvas drawImage (bilinear). Пустые имена → текстура-заглушка (magenta),
 *  чтобы было явно видно что-то не загрузилось.
 *
 *  Используется для material-palette (brick/concrete/plaster/wood/grass/
 *  dirt/stone/metal/glass). Клиент вместо 9 отдельных sampler2D использует
 *  один sampler с индексом материала из texMode. */
export async function buildMaterialAtlas(
  names: string[],
  ext: string = "jpg",
): Promise<Texture> {
  const slot = MATERIAL_ATLAS_SLOT_SIZE;
  const atlasW = slot * names.length;
  const atlasH = slot;
  const canvas = document.createElement("canvas");
  canvas.width = atlasW;
  canvas.height = atlasH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("canvas 2d ctx unavailable");
  // Заливаем магентой — так слоты с отсутствующими текстурами видно.
  ctx.fillStyle = "#ff00ff";
  ctx.fillRect(0, 0, atlasW, atlasH);
  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    if (!name) continue;
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`load texture ${name}.${ext}`));
        img.src = `/textures/${name}.${ext}`;
      });
      ctx.drawImage(img, i * slot, 0, slot, slot);
      // Также кладём в CPU-cache (loadTexture/getTexture) — нужно для
      // sampleChunkCubeColor при спавне падающих кубиков.
      if (!cache.has(name)) {
        const sc = document.createElement("canvas");
        sc.width = img.naturalWidth; sc.height = img.naturalHeight;
        const sx = sc.getContext("2d", { willReadFrequently: true });
        if (sx) {
          sx.drawImage(img, 0, 0);
          const d = sx.getImageData(0, 0, sc.width, sc.height);
          cache.set(name, { width: sc.width, height: sc.height, pixels: d.data });
        }
      }
    } catch (err) {
      console.warn(`[materialAtlas] slot ${i} (${name}): ${String(err)} — оставляем magenta`);
    }
  }
  const data = ctx.getImageData(0, 0, atlasW, atlasH);
  return {
    width: atlasW,
    height: atlasH,
    pixels: data.data,
  };
}

/** Средний цвет в прямоугольнике [u0..u1] × [v0..v1] нормированных UV.
 * Каждый воксельный кубик сэмплит свою "полоску" текстуры — это даёт
 * осмысленный цвет (медиана зоны), а не случайный пиксель из неё. */
export function sampleAvg(t: Texture, u0: number, v0: number, u1: number, v1: number): [number, number, number] {
  const w = t.width, h = t.height;
  // wrap нижнюю границу и ограничим верхнюю
  const x0 = Math.max(0, Math.floor((u0 - Math.floor(u0)) * w));
  const y0 = Math.max(0, Math.floor((v0 - Math.floor(v0)) * h));
  let x1 = Math.min(w, Math.ceil((u1 - Math.floor(u0)) * w));
  let y1 = Math.min(h, Math.ceil((v1 - Math.floor(v0)) * h));
  if (x1 <= x0) x1 = x0 + 1;
  if (y1 <= y0) y1 = y0 + 1;
  let r = 0, g = 0, b = 0, n = 0;
  for (let y = y0; y < y1; y++) {
    const rowBase = y * w * 4;
    for (let x = x0; x < x1; x++) {
      const i = rowBase + x * 4;
      r += t.pixels[i];
      g += t.pixels[i + 1];
      b += t.pixels[i + 2];
      n++;
    }
  }
  if (n === 0) return [0, 0, 0];
  return [r / n / 255, g / n / 255, b / n / 255];
}
